if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.initVariantMediaFilter();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    initVariantMediaFilter() {
      const activeVariantMedia = this.elements.viewer.querySelector('.is-active[data-variant-media-start="true"]');
      if (!activeVariantMedia) return;

      this.filterVariantMedia(activeVariantMedia.dataset.productMediaId);
    }

    onSlideChanged(event) {
      if (!event.detail.currentElement) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia || activeMedia.hidden) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (!activeThumbnail) return;
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    filterVariantMedia(featuredMediaId) {
      const visibleMediaIds = this.getVariantMediaIds(featuredMediaId);

      if (!visibleMediaIds) {
        this.showAllVariantMedia();
        return;
      }

      this.toggleMediaItems(this.elements.viewer.querySelectorAll('[data-product-media-id]'), visibleMediaIds);

      if (this.elements.thumbnails) {
        this.toggleMediaItems(this.elements.thumbnails.querySelectorAll('[data-product-media-id]'), visibleMediaIds);
      }

      this.filterModalMedia(visibleMediaIds);
      this.syncFilteredSliders(visibleMediaIds.size);
    }

    showAllVariantMedia() {
      const visibleMediaIds = new Set(
        Array.from(this.elements.viewer.querySelectorAll('[data-product-media-id]')).map((item) => item.dataset.productMediaId)
      );

      this.toggleMediaItems(this.elements.viewer.querySelectorAll('[data-product-media-id]'), visibleMediaIds);

      if (this.elements.thumbnails) {
        this.toggleMediaItems(this.elements.thumbnails.querySelectorAll('[data-product-media-id]'), visibleMediaIds);
      }

      this.filterModalMedia(visibleMediaIds);
      this.syncFilteredSliders(visibleMediaIds.size);
    }

    getVariantMediaIds(featuredMediaId) {
      const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-product-media-id][data-product-media-position]'));
      const mediaStartItems = mediaItems
        .filter((item) => item.dataset.variantMediaStart === 'true')
        .sort((itemA, itemB) => Number(itemA.dataset.productMediaPosition) - Number(itemB.dataset.productMediaPosition));

      if (!mediaItems.length || !mediaStartItems.length) return null;

      const activeStartItem = mediaStartItems.find((item) => item.dataset.productMediaId === String(featuredMediaId));
      if (!activeStartItem) return null;

      const startPosition = Number(activeStartItem.dataset.productMediaPosition);
      const nextStartItem = mediaStartItems.find((item) => Number(item.dataset.productMediaPosition) > startPosition);
      const endPosition = nextStartItem ? Number(nextStartItem.dataset.productMediaPosition) : Infinity;
      const firstStartPosition = Number(mediaStartItems[0].dataset.productMediaPosition);
      const visibleMediaIds = new Set(
        mediaItems
          .filter((item) => {
            const mediaPosition = Number(item.dataset.productMediaPosition);
            const isSharedLeadingMedia = mediaPosition < firstStartPosition;
            const isVariantMedia = mediaPosition >= startPosition && mediaPosition < endPosition;

            return isSharedLeadingMedia || isVariantMedia;
          })
          .map((item) => item.dataset.productMediaId)
      );

      return visibleMediaIds.size ? visibleMediaIds : null;
    }

    toggleMediaItems(mediaItems, visibleMediaIds) {
      mediaItems.forEach((item) => {
        const isVisible = visibleMediaIds.has(item.dataset.productMediaId);
        item.hidden = !isVisible;
        item.setAttribute('aria-hidden', String(!isVisible));

        if (!isVisible) {
          item.classList.remove('is-active');
        }
      });
    }

    filterModalMedia(visibleMediaIds) {
      const sectionId = this.id.replace('MediaGallery-', '');
      const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
      if (!modalContent) return;

      modalContent.querySelectorAll('[data-media-id]').forEach((item) => {
        item.hidden = !visibleMediaIds.has(item.dataset.mediaId);
        if (item.hidden) item.classList.remove('active');
      });
    }

    syncFilteredSliders(visibleMediaCount) {
      [this.elements.viewer, this.elements.thumbnails].forEach((sliderComponent) => {
        if (!sliderComponent) return;

        if (sliderComponent.slider) sliderComponent.slider.scrollTo({ left: 0 });
        if (sliderComponent.resetPages) sliderComponent.resetPages();

        if (sliderComponent.currentPageElement) sliderComponent.currentPageElement.textContent = '1';
        if (sliderComponent.pageTotalElement) sliderComponent.pageTotalElement.textContent = String(Math.max(visibleMediaCount, 1));

        if (visibleMediaCount <= 1) {
          if (sliderComponent.prevButton) sliderComponent.prevButton.setAttribute('disabled', 'disabled');
          if (sliderComponent.nextButton) sliderComponent.nextButton.setAttribute('disabled', 'disabled');
        }
      });
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
